/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useQuery, useLazyQuery } from "@apollo/client";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import {
  GET_USER_SUBSCRIPTIONS_BY_USER,
  GET_USER_SUBSCRIPTIONS_BY_USER_NEXT,
  GET_USER_SUBSCRIPTIONS_BY_USER_PREV,
} from "graphql/query/admin";
import Table from "Components/Table/Table";

const STATUS_OPTIONS = ["ACTIVE", "INACTIVE", "EXPIRED", "CANCELLED", "PENDING"];

const UserSubscriptionTab = ({ userId }) => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [pageInfo, setPageInfo] = useState("");
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ status: "", isCancelledBool: "", isFreeBool: "" });
  const [appliedFilters, setAppliedFilters] = useState({});

  const buildFilters = () => {
    const f = {};
    if (appliedFilters.status) f.status = appliedFilters.status;
    if (appliedFilters.isCancelledBool !== "" && appliedFilters.isCancelledBool !== undefined) {
      f.isCancelledBool = appliedFilters.isCancelledBool;
    }
    if (appliedFilters.isFreeBool !== "" && appliedFilters.isFreeBool !== undefined) {
      f.isFreeBool = appliedFilters.isFreeBool;
    }
    return f;
  };

  const { data, refetch, error, loading } = useQuery(GET_USER_SUBSCRIPTIONS_BY_USER, {
    variables: {
      userId,
      first: 10,
      orderBy: "createdAt_DESC",
      filters: buildFilters(),
    },
    fetchPolicy: "network-only",
    skip: !userId,
  });

  const [getNextPage, { loading: nextLoader }] = useLazyQuery(
    GET_USER_SUBSCRIPTIONS_BY_USER_NEXT
  );
  const [getPrevPage, { loading: prevLoader }] = useLazyQuery(
    GET_USER_SUBSCRIPTIONS_BY_USER_PREV
  );

  const mapEdges = (edges) =>
    edges.map((e) => ({
      ...e.node,
      isFreeDisplay: e.node.isFree ? "Yes" : "No",
      isCancelledDisplay: e.node.isCancelled ? "Yes" : "No",
    }));

  const handleNextPage = async () => {
    if (pageInfo.hasNextPage) {
      const res = await getNextPage({
        variables: {
          userId,
          first: 10,
          after: pageInfo.endCursor,
          orderBy: "createdAt_DESC",
          filters: buildFilters(),
        },
        fetchPolicy: "network-only",
      });
      if (res.data?.userSubscriptionsByUser) {
        setSubscriptions(mapEdges(res.data.userSubscriptionsByUser.edges));
        setPage(page + 1);
        setPageInfo(res.data.userSubscriptionsByUser.pageInfo);
      }
    }
  };

  const handlePreviousPage = async () => {
    if (pageInfo.hasPreviousPage) {
      const res = await getPrevPage({
        variables: {
          userId,
          last: 10,
          before: pageInfo.startCursor,
          orderBy: "createdAt_DESC",
          filters: buildFilters(),
        },
        fetchPolicy: "network-only",
      });
      if (res.data?.userSubscriptionsByUser) {
        setSubscriptions(mapEdges(res.data.userSubscriptionsByUser.edges));
        setPage(page - 1);
        setPageInfo(res.data.userSubscriptionsByUser.pageInfo);
      }
    }
  };

  useEffect(() => {
    if (data?.userSubscriptionsByUser) {
      setSubscriptions(mapEdges(data.userSubscriptionsByUser.edges));
      setPageInfo(data.userSubscriptionsByUser.pageInfo);
    }
    setPage(0);
  }, [data]);

  const handleApplyFilter = () => {
    setAppliedFilters({ ...filters });
    setTimeout(() => refetch(), 100);
  };

  const handleResetFilter = () => {
    setFilters({ status: "", isCancelledBool: "", isFreeBool: "" });
    setAppliedFilters({});
    setTimeout(() => refetch(), 100);
  };

  const handleViewButton = (id) => {
    const subscription = subscriptions.find((s) => s.id === id);
    navigate("/admin/user/view-user-subscription", {
      state: { subscriptionId: id, userId, subscription },
    });
  };

  const TABLE_HEAD = [
    { id: "id", flag: "id", label: "No.", alignRight: false },
    { id: "subscriptionId", flag: "subscriptionId", label: "Subscription ID", alignRight: false },
    { id: "status", flag: "status", label: "Status", alignRight: false },
    { id: "isFreeDisplay", flag: "isFreeDisplay", label: "Free", alignRight: false },
    { id: "isCancelledDisplay", flag: "isCancelledDisplay", label: "Cancelled", alignRight: false },
    { id: "grantedVia", flag: "grantedVia", label: "Granted Via", alignRight: false },
    { id: "startDate", flag: "startDate", label: "Start Date", alignRight: false },
    { id: "endDate", flag: "endDate", label: "End Date", alignRight: false },
    { id: "createdAt", flag: "createdAt", label: "Created At", alignRight: false },
    {
      id: "actions",
      flag: { read: true },
      callback: { handleViewButton },
      label: "Actions",
      alignRight: false,
    },
  ];

  return (
    <Box style={{ padding: "16px 0" }}>
      {/* Filters */}
      <Grid container spacing={2} style={{ marginBottom: 16, padding: "0 8px" }}>
        <Grid item xs={12} sm={3}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Free</InputLabel>
            <Select
              label="Free"
              value={filters.isFreeBool}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  isFreeBool: e.target.value === "" ? "" : e.target.value === "true",
                })
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Cancelled</InputLabel>
            <Select
              label="Cancelled"
              value={filters.isCancelledBool}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  isCancelledBool: e.target.value === "" ? "" : e.target.value === "true",
                })
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApplyFilter}
            style={{ background: "var(--color-primary, #1976d2)" }}
          >
            Apply
          </Button>
          <Button variant="outlined" onClick={handleResetFilter}>
            Reset
          </Button>
        </Grid>
      </Grid>

      <Table
        error={error}
        USERLIST={subscriptions}
        page={page}
        handleNextPage={handleNextPage}
        handlePreviousPage={handlePreviousPage}
        TABLE_HEAD={TABLE_HEAD}
        pageInfo={pageInfo}
        loading={loading || nextLoader || prevLoader}
      />
    </Box>
  );
};

export default UserSubscriptionTab;
